import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi, getBridgeOid, getBridgeEmail } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import { useProject } from '../context/ProjectContext';
import { Icon, Chevron } from '../components/icons';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { exportReviewExcel, exportReviewGherkin, exportReviewTxt } from '../utils/exportUtils';
import type { SessionDetail, TestCase } from '../types';

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';
const DEV_EMAIL  = 'dev@fintinc.com';

type EditDraft = Partial<TestCase>;

// Grows a textarea to fit its content so edit fields never need an internal scrollbar.
function autoGrow(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

export default function ReviewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { instance, accounts } = useMsal();
  const cfg           = useRuntimeConfig();
  const navigate      = useNavigate();
  const api           = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast } = useToast();
  const confirmDialog  = useConfirm();
  const { projects: allProjects } = useProject();

  const [session, setSession]     = useState<SessionDetail | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [editingId, setEditingId]         = useState<string | null>(null);
  const [draft, setDraft]                 = useState<EditDraft>({});
  const [saving, setSaving]               = useState<string | null>(null);
  const [pushing, setPushing]             = useState(false);
  const [approvingAll, setApprovingAll]   = useState(false);
  const [rejectTarget, setRejectTarget]   = useState<string | null>(null);
  const [rejectReason, setRejectReason]   = useState('');
  const [exportOpen, setExportOpen]       = useState(false);
  const [exportMenuPos, setExportMenuPos] = useState<{ top: number; right: number } | null>(null);

  // Row truncation (Steps/Test Data/Expected Result collapse to a 2-line preview)
  const [expandedRows, setExpandedRows]   = useState<Set<string>>(new Set());
  // Reveal-toggle for masked credentials in Test Data
  const [revealedIds, setRevealedIds]     = useState<Set<string>>(new Set());
  // Bulk selection
  const [selectedIds, setSelectedIds]     = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy]           = useState(false);
  // Filter bar
  const [statusFilter, setStatusFilter]   = useState('');
  const [typeFilter, setTypeFilter]       = useState('');
  const [search, setSearch]               = useState('');

  const titleRef      = useRef<HTMLInputElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const exportBtnRef  = useRef<HTMLButtonElement>(null);

  useEffect(() => { loadSession(); }, [sessionId]);

  useEffect(() => {
    if (!exportOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [exportOpen]);

  // Export menu is position:fixed (JS-positioned) because .review-summary-bar has
  // overflow:hidden for its gradient top strip, which would clip an absolutely-positioned menu.
  useLayoutEffect(() => {
    if (!exportOpen) return;
    function updatePos() {
      const rect = exportBtnRef.current?.getBoundingClientRect();
      if (rect) setExportMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [exportOpen]);

  async function loadSession() {
    try {
      const data = await api.getSession(sessionId!);
      setSession(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function decide(caseId: string, decision: 'approved' | 'rejected' | 'edited', editedCase?: Partial<TestCase>, rejectionReason?: string) {
    setSaving(caseId);
    try {
      await api.patchCase(sessionId!, caseId, { decision, editedCase, rejectionReason });
      await loadSession();
    } catch (e: any) {
      showToast('Failed to save decision: ' + e.message, 'error');
    } finally {
      setSaving(null);
      if (editingId === caseId) {
        setEditingId(null);
        setDraft({});
      }
    }
  }

  function openRejectModal(caseId: string) {
    setRejectTarget(caseId);
    setRejectReason('');
  }

  async function confirmReject() {
    if (!rejectTarget || !rejectReason.trim()) return;
    const caseId = rejectTarget;
    setRejectTarget(null);
    await decide(caseId, 'rejected', undefined, rejectReason.trim());
    setRejectReason('');
  }

  function cancelReject() {
    setRejectTarget(null);
    setRejectReason('');
  }

  function startEdit(tc: TestCase) {
    setEditingId(tc.id);
    setDraft({
      title: tc.title,
      preconditions: tc.preconditions,
      steps: [...tc.steps],
      testData: tc.testData,
      expectedResult: tc.expectedResult,
    });
    setTimeout(() => titleRef.current?.focus(), 50);
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft({});
  }

  async function saveEdit(caseId: string) {
    await decide(caseId, 'edited', draft);
  }

  async function handlePush() {
    if (!await confirmDialog('Push all approved cases to the configured target?', { confirmLabel: 'Push' })) return;
    setPushing(true);
    try {
      const results = await api.pushSession(sessionId!);
      const ok  = results.filter(r => r.success).length;
      const bad = results.filter(r => !r.success).length;
      const failMessages = results.filter(r => !r.success).map(r => `${r.caseId}: ${r.error}`).join('\n');
      showToast(`Push complete: ${ok} succeeded${bad ? `, ${bad} failed:\n${failMessages}` : ''}.`, bad ? 'error' : 'success');
      await loadSession();
    } catch (e: any) {
      showToast('Push failed: ' + e.message, 'error');
    } finally {
      setPushing(false);
    }
  }

  // TEMP DEV-ONLY: bulk-approve every pending case without per-case review, for local push testing.
  // Gated on import.meta.env.DEV so it never appears in the deployed QA/Stage build. Remove once
  // a real "Approve All" backend endpoint exists.
  async function approveAll() {
    if (!session) return;
    const toApprove = session.cases.filter(c => c.status === 'pending');
    if (!toApprove.length) return;
    setApprovingAll(true);
    try {
      for (const tc of toApprove) {
        await api.patchCase(sessionId!, tc.id, { decision: 'approved' });
      }
      await loadSession();
    } catch (e: any) {
      showToast('Approve all failed: ' + e.message, 'error');
    } finally {
      setApprovingAll(false);
    }
  }

  function handleExport(format: 'excel' | 'gherkin' | 'txt') {
    if (!session || !session.cases.length) return;
    setExportOpen(false);
    if (format === 'excel')        exportReviewExcel(session.cases, session.title);
    else if (format === 'gherkin') exportReviewGherkin(session.cases, session.title);
    else                            exportReviewTxt(session.cases, session.title);
  }

  function toggleExpand(caseId: string) {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(caseId) ? next.delete(caseId) : next.add(caseId);
      return next;
    });
  }

  function toggleReveal(caseId: string) {
    setRevealedIds(prev => {
      const next = new Set(prev);
      next.has(caseId) ? next.delete(caseId) : next.add(caseId);
      return next;
    });
  }

  function toggleSelect(caseId: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(caseId) ? next.delete(caseId) : next.add(caseId);
      return next;
    });
  }

  function toggleSelectAll(visibleIds: string[]) {
    setSelectedIds(prev => {
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  }

  async function bulkApprove() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      for (const id of ids) await api.patchCase(sessionId!, id, { decision: 'approved' });
      setSelectedIds(new Set());
      await loadSession();
    } catch (e: any) {
      showToast('Approve selected failed: ' + e.message, 'error');
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkReject() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    // A single case still gets the reason modal (openRejectModal/confirmReject below) so
    // reviewers can record why — a shared reason across a multi-select batch doesn't make sense.
    if (ids.length === 1) { openRejectModal(ids[0]); return; }
    if (!await confirmDialog(`Reject ${ids.length} selected test cases?`, { confirmLabel: 'Reject', danger: true })) return;
    setBulkBusy(true);
    try {
      for (const id of ids) await api.patchCase(sessionId!, id, { decision: 'rejected' });
      setSelectedIds(new Set());
      await loadSession();
    } catch (e: any) {
      showToast('Reject selected failed: ' + e.message, 'error');
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkDelete() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!await confirmDialog(`Delete ${ids.length} selected test case${ids.length !== 1 ? 's' : ''}? This cannot be undone.`, { confirmLabel: 'Delete', danger: true })) return;
    setBulkBusy(true);
    try {
      for (const id of ids) await api.deleteCase(sessionId!, id);
      setSelectedIds(new Set());
      await loadSession();
    } catch (e: any) {
      showToast('Delete selected failed: ' + e.message, 'error');
    } finally {
      setBulkBusy(false);
    }
  }

  // Matches "…someone@domain.com … password: <value>…" style Test Data strings so the
  // secret token can be masked by default — deliberately narrow (email + explicit
  // "password" label) so it doesn't false-positive on card numbers/coupon codes/etc.
  function maskTestDataCredential(raw: string): { prefix: string; secret: string; suffix: string } | null {
    const m = raw.match(/[\w.+-]+@[\w-]+\.[a-zA-Z]{2,}[\s\S]*?password\s*:?\s*([^\s|]+)/i);
    if (!m) return null;
    const secret = m[1];
    const secretStart = raw.indexOf(secret, m.index! + m[0].length - secret.length);
    if (secretStart === -1) return null;
    return {
      prefix: raw.slice(0, secretStart),
      secret,
      suffix: raw.slice(secretStart + secret.length),
    };
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading session…</div>;
  if (error)   return <div className="page-error">Failed to load session: {error}</div>;
  if (!session) return null;

  const currentEmail       = (DEV_BYPASS ? DEV_EMAIL : (accounts[0]?.username ?? getBridgeEmail())).toLowerCase();
  const currentOid         = DEV_BYPASS ? '' : (accounts[0]?.localAccountId ?? getBridgeOid());
  const isAuthor           = session.authorEmail.toLowerCase() === currentEmail
                          || (!!currentOid && session.authorOid === currentOid);
  const isAssignedReviewer = (session.assignedReviewers ?? [])
                               .some(r => r.toLowerCase() === currentEmail);

  const cases    = session.cases;
  const approved = cases.filter(c => c.status === 'approved').length;
  const rejected = cases.filter(c => c.status === 'rejected').length;
  const pushed   = cases.filter(c => c.status === 'pushed').length;
  const pending  = cases.filter(c => c.status === 'pending').length;
  const canPush  = isAuthor && approved > 0 && pending === 0 && session.status !== 'pushed';

  const statusBreakdown = [
    approved > 0 && `${approved} Approved`,
    rejected > 0 && `${rejected} Rejected`,
    pending  > 0 && `${pending} Pending`,
  ].filter(Boolean).join(' · ');

  const searchQuery = search.trim().toLowerCase();
  const filteredCases = cases.filter(tc => {
    if (statusFilter && tc.status !== statusFilter) return false;
    if (typeFilter && tc.type !== typeFilter) return false;
    if (searchQuery && !tc.id.toLowerCase().includes(searchQuery) && !tc.title.toLowerCase().includes(searchQuery)) return false;
    return true;
  });
  const hasFilter = !!(statusFilter || typeFilter || searchQuery);

  const canBulkAct     = isAuthor || isAssignedReviewer;
  const visibleIds     = filteredCases.map(tc => tc.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const selectedCount  = selectedIds.size;
  const soleSelected   = selectedCount === 1 ? cases.find(c => c.id === [...selectedIds][0]) ?? null : null;
  const canEditSole    = isAuthor && !!soleSelected
                       && (soleSelected.status === 'pending' || soleSelected.status === 'rejected');

  return (
    <div className="page review-page">
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/')}><Icon name="arrow-left" size={13} /> Dashboard</button>
          <h1 className="page-title">{session.title}</h1>
          <p className="page-subtitle">
            {isAuthor ? (
              <>My submission · <strong>{session.outputType}</strong></>
            ) : isAssignedReviewer ? (
              <>
                Assigned to you for review · Submitted by{' '}
                <strong>{session.authorName || session.authorEmail}</strong>
                {' '}({session.authorEmail}) · {session.outputType}
                {session.createdAt && (
                  <> · Submitted on{' '}
                    <strong>{new Date(session.createdAt).toLocaleString(undefined, {
                      year: 'numeric', month: 'short', day: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}</strong>
                  </>
                )}
              </>
            ) : (
              <>
                View only · Submitted by{' '}
                <strong>{session.authorName || session.authorEmail}</strong>
                {' '}({session.authorEmail}) · {session.outputType}
              </>
            )}
          </p>
          {session.comment && (
            <div className="session-reviewer-comment">
              <span className="session-reviewer-comment-label">Reviewer note:</span> {session.comment}
            </div>
          )}
          {session.projectId && (
            <p className="hint-text" style={{ marginTop: 6 }}>
              <Icon name="layers" size={12} /> Generated with project context:{' '}
              <strong>{allProjects.find(p => p.id === session.projectId)?.name ?? 'unknown project'}</strong>
              {session.contextVersionId && (
                <span className="mono"> · context {session.contextVersionId.replace(/^v0*/, 'v')}</span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Summary bar */}
      <div className="review-summary-bar">
        <div className="review-summary-counts">
          {session.status === 'pushed' && (
            <span className="badge badge-pushed-session">Pushed</span>
          )}
          {statusBreakdown && <span className="count-chip chip-breakdown">{statusBreakdown}</span>}
          {pushed > 0 && <span className="count-chip chip-pushed">{pushed} pushed</span>}
        </div>
        <div className="review-summary-actions">
          {import.meta.env.DEV && pending > 0 && (
            <button className="btn btn-outline" onClick={approveAll} disabled={approvingAll}>
              {approvingAll ? 'Approving…' : `[DEV] Approve All (${pending})`}
            </button>
          )}
          {canBulkAct && selectedCount > 0 && (
            <>
              <span className="count-chip bulk-action-count">{selectedCount} selected</span>
              {isAssignedReviewer && !editingId && (
                <>
                  <button className="btn btn-sm btn-primary" onClick={bulkApprove} disabled={bulkBusy}>
                    <Icon name="check" size={13} /> Approve selected
                  </button>
                  <button className="btn btn-sm btn-outline" onClick={bulkReject} disabled={bulkBusy}>
                    <Icon name="x" size={13} /> Reject selected
                  </button>
                </>
              )}
              {editingId ? (
                <>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => saveEdit(editingId)}
                    disabled={saving === editingId}
                    title="Save changes"
                  >
                    <Icon name="check" size={13} /> {saving === editingId ? 'Saving…' : 'Save'}
                  </button>
                  <button className="btn btn-sm btn-outline" onClick={cancelEdit} disabled={saving === editingId}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {isAuthor && (
                    <button className="btn btn-sm btn-danger" onClick={bulkDelete} disabled={bulkBusy}>
                      <Icon name="trash" size={13} /> Delete selected
                    </button>
                  )}
                  {canEditSole && (
                    <button className="btn btn-sm btn-outline" onClick={() => soleSelected && startEdit(soleSelected)}>
                      <Icon name="edit" size={13} /> Edit
                    </button>
                  )}
                </>
              )}
              <button className="btn btn-sm btn-outline" onClick={() => setSelectedIds(new Set())} disabled={!!editingId}>
                Clear
              </button>
              <span className="bulk-action-divider" />
            </>
          )}
          <div className="export-menu-wrapper" ref={exportMenuRef}>
            <button
              ref={exportBtnRef}
              className="btn btn-outline"
              onClick={() => setExportOpen(o => !o)}
              disabled={cases.length === 0}
              title={cases.length === 0 ? 'No test cases to export' : undefined}
            >
              <Icon name="download" size={13} /> Export <Icon name="chevron-down" size={13} />
            </button>
            {exportOpen && exportMenuPos && (
              <div className="export-menu" style={{ top: exportMenuPos.top, right: exportMenuPos.right }}>
                <button className="export-menu-item" onClick={() => handleExport('excel')}>
                  <Icon name="document" size={14} /> Excel (TDD)
                </button>
                <button className="export-menu-item" onClick={() => handleExport('gherkin')}>
                  <Icon name="document" size={14} /> Gherkin (.feature)
                </button>
                <button className="export-menu-item" onClick={() => handleExport('txt')}>
                  <Icon name="document" size={14} /> Plain Text (.txt)
                </button>
              </div>
            )}
          </div>
          {canPush && (
            <button className="btn btn-primary" onClick={handlePush} disabled={pushing}>
              {pushing ? 'Pushing…' : `Push Approved (${approved})`}
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <label className="filter-label">Status</label>
        <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="pending">Pending</option>
        </select>

        <label className="filter-label">Type</label>
        <select className="filter-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All</option>
          <option value="Positive">Positive</option>
          <option value="Negative">Negative</option>
        </select>

        <input
          className="form-input"
          style={{ maxWidth: 220 }}
          placeholder="Search by ID or title…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {hasFilter && (
          <button className="btn btn-sm btn-outline" onClick={() => { setStatusFilter(''); setTypeFilter(''); setSearch(''); }}>
            Clear filters
          </button>
        )}
        <span className="filter-count">{filteredCases.length} of {cases.length} case{cases.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Test case table */}
      <div className="review-table-wrapper">
        <table className="review-table">
          <thead>
            <tr>
              <th className="web-auto-expand-cell" />
              {canBulkAct && (
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => toggleSelectAll(visibleIds)}
                    disabled={!!editingId}
                    title="Select all"
                  />
                </th>
              )}
              <th style={{ width: 90 }}>ID</th>
              <th style={{ width: 200 }}>Title</th>
              <th style={{ width: 80 }}>Type</th>
              <th>Preconditions</th>
              <th>Steps</th>
              <th>Test Data</th>
              <th>Expected Result</th>
              <th style={{ width: 110 }}>Status</th>
              <th style={{ width: 200 }}>Comments</th>
              <th style={{ width: 170 }}>Reviewed By</th>
            </tr>
          </thead>
          <tbody>
            {filteredCases.length === 0 && (
              <tr>
                <td colSpan={canBulkAct ? 12 : 11} className="empty-table-row">
                  No test cases match the current filters.
                </td>
              </tr>
            )}
            {filteredCases.map((tc, idx) => {
              const isEditing  = editingId === tc.id;
              const rowCls     = `tc-row tc-${isEditing ? 'editing' : tc.status}`;

              const isExpanded = expandedRows.has(tc.id);
              const clampCls   = isEditing || isExpanded ? '' : 'tc-clamp';
              const maskedTestData = isEditing ? null : maskTestDataCredential(tc.testData ?? '');
              const isRevealed = revealedIds.has(tc.id);

              return (
                <tr key={tc.id ?? `case-${idx}`} className={rowCls}>
                  <td className="web-auto-expand-cell">
                    <button
                      type="button"
                      className="web-auto-expand-btn"
                      onClick={() => toggleExpand(tc.id)}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <Chevron open={isExpanded} />
                    </button>
                  </td>
                  {canBulkAct && (
                    <td className="tc-cell" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(tc.id)}
                        onChange={() => toggleSelect(tc.id)}
                        disabled={!!editingId}
                      />
                    </td>
                  )}
                  <td className="tc-cell tc-id-cell">{tc.id}</td>

                  {/* Title */}
                  <td className="tc-cell">
                    {isEditing
                      ? <input ref={titleRef} className="edit-input" value={draft.title ?? ''} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} />
                      : <span className={tc.status === 'rejected' ? 'strikethrough' : ''}>{tc.title}</span>}
                  </td>

                  {/* Type */}
                  <td className="tc-cell"><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>

                  {/* Preconditions */}
                  <td className="tc-cell">
                    {isEditing
                      ? <textarea
                          className="edit-textarea" rows={1} ref={autoGrow}
                          value={draft.preconditions ?? ''}
                          onChange={e => { setDraft(d => ({ ...d, preconditions: e.target.value })); autoGrow(e.target); }}
                        />
                      : <span>{tc.preconditions}</span>}
                  </td>

                  {/* Steps */}
                  <td className="tc-cell tc-steps">
                    {isEditing
                      ? <textarea
                          className="edit-textarea" rows={1} ref={autoGrow}
                          value={(draft.steps || []).map((s, i) => `${i + 1}. ${s}`).join('\n')}
                          onChange={e => {
                            const lines = e.target.value.split('\n').map(l => l.replace(/^\d+\.\s*/, '').trim()).filter(Boolean);
                            setDraft(d => ({ ...d, steps: lines }));
                            autoGrow(e.target);
                          }}
                        />
                      : <div className={clampCls}>
                          <ol className="steps-list">{tc.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
                        </div>}
                  </td>

                  {/* Test Data */}
                  <td className="tc-cell">
                    {isEditing
                      ? <input className="edit-input" value={draft.testData ?? ''} onChange={e => setDraft(d => ({ ...d, testData: e.target.value }))} />
                      : <div className={clampCls}>
                          {maskedTestData ? (
                            <span>
                              {maskedTestData.prefix}
                              {isRevealed ? maskedTestData.secret : '•'.repeat(Math.min(maskedTestData.secret.length, 10))}
                              <button
                                type="button"
                                className="reveal-toggle"
                                title={isRevealed ? 'Hide value' : 'Reveal value'}
                                onClick={() => toggleReveal(tc.id)}
                              >
                                <Icon name={isRevealed ? 'eyeoff' : 'eye'} size={12} />
                              </button>
                              {maskedTestData.suffix}
                            </span>
                          ) : tc.testData}
                        </div>}
                  </td>

                  {/* Expected Result */}
                  <td className="tc-cell">
                    {isEditing
                      ? <textarea
                          className="edit-textarea" rows={1} ref={autoGrow}
                          value={draft.expectedResult ?? ''}
                          onChange={e => { setDraft(d => ({ ...d, expectedResult: e.target.value })); autoGrow(e.target); }}
                        />
                      : <div className={clampCls}>{tc.expectedResult}</div>}
                  </td>

                  {/* Status */}
                  <td className="tc-cell tc-status-cell">
                    <span className={`tc-status-badge tc-status-${tc.status}`}>
                      {tc.status.charAt(0).toUpperCase() + tc.status.slice(1)}
                    </span>
                  </td>

                  {/* Comments */}
                  <td className="tc-cell tc-comments">
                    {tc.rejectionReason
                      ? <span className="rejection-reason">{tc.rejectionReason}</span>
                      : <span className="no-comment">—</span>}
                  </td>

                  {/* Reviewed By */}
                  <td className="tc-cell tc-reviewers">
                    {(tc.approvals ?? []).map(r => (
                      <span key={r.oid} className="reviewer-chip chip-approver" title={`${r.name} (${r.email})\n${new Date(r.timestamp).toLocaleString()}`}>
                        {(r.name || r.email)[0].toUpperCase()}
                      </span>
                    ))}
                    {(tc.rejections ?? []).map(r => (
                      <span key={r.oid} className="reviewer-chip chip-rejecter" title={`${r.name} (${r.email})\n${new Date(r.timestamp).toLocaleString()}`}>
                        {(r.name || r.email)[0].toUpperCase()}
                      </span>
                    ))}
                    {tc.status === 'pushed' && tc.remoteKey && (
                      <span className="remote-key">{tc.remoteKey}</span>
                    )}
                    {(tc.approvals ?? []).length === 0 && (tc.rejections ?? []).length === 0 && (
                      <span className="no-comment">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Reject reason modal */}
      {rejectTarget && (
        <Modal
          title="Reason for Rejection"
          onClose={cancelReject}
          footer={
            <>
              <button className="btn btn-outline" onClick={cancelReject}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmReject} disabled={!rejectReason.trim()}>Reject</button>
            </>
          }
        >
          <div className="form-group">
            <label>Please provide a reason for rejection</label>
            <textarea
              className="edit-textarea"
              rows={4}
              autoFocus
              placeholder="Describe why this test case is being rejected…"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey && rejectReason.trim()) confirmReject(); }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
